import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { isSmsRefused, smsRefusedSentence } from '../../utils/smsRefusal'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'

export default function ResetPasswordVerify() {
  const navigate = useNavigate()
  const location = useLocation()
  const { verifyResetCode, resendResetCode, loading, error: storeError, clearError } = useUserStore()
  const { t } = useI18n()
  
  // Get data from navigation state
  const { userId, resetToken, smsSent, maskedPhone, phone } = location.state || {}
  
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [resendTimer, setResendTimer] = useState(30)
  const [verifying, setVerifying] = useState(false)
  const inputRef = useRef(null)
  
  // Clear errors on mount
  useEffect(() => {
    clearError()
    setError('')
  }, [])
  
  // Redirect if no userId
  useEffect(() => {
    if (!userId) {
      navigate('/forgot-password', { replace: true })
    }
  }, [userId, navigate])
  
  useEffect(() => {
    inputRef.current?.focus()
    const timer = setInterval(() => {
      setResendTimer(prev => prev > 0 ? prev - 1 : 0)
    }, 1000)
    return () => clearInterval(timer)
  }, [])
  
  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
    setError('')
  }
  
  const handleVerify = async () => {
    if (code.length !== 6) {
      setError(t('auth.enterFullCode'))
      return
    }
    
    setVerifying(true)
    setError('')
    
    const result = await verifyResetCode(userId, code, resetToken, phone)
    
    if (result.success) {
      // Navigate to new password screen
      navigate('/forgot-password/new-password', {
        state: {
          userId,
          resetAuthToken: result.resetAuthToken
        },
        replace: true
      })
    } else {
      setError(result.error || t('auth.invalidCode'))
      setVerifying(false)
    }
  }
  
  const handleResend = async () => {
    if (resendTimer > 0) return
    
    const r = await resendResetCode(userId)
    // PROD-807: a refusal sent nothing - say why, start no cooldown, keep the field.
    if (isSmsRefused(r)) {
      setError(smsRefusedSentence(r) || t('auth.invalidCode'))
      return
    }
    
    setResendTimer(30)
    setCode('')
    inputRef.current?.focus()
  }
  
  // Split code into array for display
  const codeDigits = code.split('')
  
  if (!userId) {
    return null
  }
  
  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
      <div className="screen-min-h bg-white px-6 pt-6 md:px-10 md:pt-10 flex flex-col">
        <button 
          onClick={() => navigate('/forgot-password')}
          className="self-start p-2 -ml-2 text-gray-600"
        >
          <ArrowLeft size={24} />
        </button>

        <div className="flex-1 flex flex-col items-center pt-8">
          <h1 className="text-2xl md:text-3xl font-bold text-orella-700 mb-2">
            {t('auth.verifyCode')}
          </h1>
          <p className="text-gray-500 text-center mb-4 md:text-lg">
            {smsSent 
              ? t('auth.resetCodeSent')
              : t('auth.smsPending')}
          </p>
          {maskedPhone && (
            <p className="text-sm text-gray-400 mb-4">{maskedPhone}</p>
          )}

          {/* Code input - single field with visual boxes */}
          <div className="relative mb-6">
            {/* Hidden actual input */}
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={handleChange}
              maxLength={6}
              className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
            />
            
            {/* Visual boxes */}
            <div className="flex gap-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div
                  key={index}
                  className={`w-12 h-14 flex items-center justify-center text-2xl font-semibold border-2 rounded-xl transition-colors ${
                    index === code.length 
                      ? 'border-orella-500 bg-orella-50' 
                      : codeDigits[index] 
                        ? 'border-orella-300 bg-white' 
                        : 'border-gray-200 bg-white'
                  }`}
                >
                  {codeDigits[index] || ''}
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* PROD-1194: Verify is pinned at the bottom; resend and cancel (two secondaries) follow it and scroll. */}
        <BottomActionBar className="-mx-6 md:-mx-10 [--bar-px:24px]"
          note={(error || storeError) ? <p className="text-red-500 text-sm mb-3 text-center">{error || storeError}</p> : null}>
          <button
            onClick={handleVerify}
            disabled={verifying || loading || code.length !== 6}
            className="w-full btn-primary disabled:opacity-50 breathe-blue"
          >
            {verifying ? t('auth.verifying') : t('auth.verify')}
          </button>
        </BottomActionBar>

        <div className="flex flex-col items-center bottom-actions [--ba-min:24px]">
          <button
            onClick={handleResend}
            disabled={resendTimer > 0 || loading}
            className={`text-sm ${resendTimer > 0 ? 'text-gray-400' : 'text-orella-500'}`}
          >
            {resendTimer > 0 ? `${t('auth.resendIn')} ${resendTimer}s` : t('auth.resendCode')}
          </button>
          
          {/* Cancel link */}
          <button
            onClick={() => navigate('/signin')}
            className="mt-6 text-gray-500 text-sm"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </ResponsiveWrapper>
  )
}
